/**
 * Reply semantics, shared by the hover bar, double-click, the R key, swipe and the context menu (consistent with
 * inline threads): replying to a top-level message opens its thread; replying to a message that is already in a
 * thread quotes it into that thread's composer. Composers listen for COMPOSE_EVENT.
 */
import type React from 'react';
import type { Message } from '@tradeoutlet/shared';

export const COMPOSE_EVENT = 'collab:compose';
export interface ComposeDetail { conversationId: string; parentId: string | null; quote?: string }

/** Where a reply goes: the thread root, and whether to quote the message (only for replies inside a thread). */
export function replyTarget(m: Pick<Message, 'id' | 'parent_id'>): { rootId: string; quote: boolean } {
  return m.parent_id ? { rootId: m.parent_id, quote: true } : { rootId: m.id, quote: false };
}

/** "> " quote of a message: mentions kept, at most 3 lines / 280 characters. */
export function quoteOf(body: string): string {
  const flat = body.replace(/```[\s\S]*?```/g, '[code]').split('\n').map(l => l.replace(/^>\s?/, '').trim()).filter(Boolean);
  let text = flat.slice(0, 3).join('\n');
  if (text.length > 280) text = `${text.slice(0, 279)}…`;
  else if (flat.length > 3) text += ' …';
  return text.split('\n').map(l => `> ${l}`).join('\n');
}

/** Start a reply: open the thread (the panel's composer focuses) and, for thread messages, quote into it. */
export function startReply(m: Pick<Message, 'id' | 'parent_id' | 'body' | 'conversation_id'>, openThread: (id: string) => void) {
  const t = replyTarget(m);
  openThread(t.rootId);
  // After the panel has rendered its composer.
  setTimeout(() => window.dispatchEvent(new CustomEvent<ComposeDetail>(COMPOSE_EVENT, {
    detail: { conversationId: m.conversation_id, parentId: t.rootId, quote: t.quote ? quoteOf(m.body) : undefined },
  })), 50);
}

/** Double-click should reply only on plain message content, not on links, buttons, inputs or media. */
export function isInteractiveTarget(t: EventTarget | null): boolean {
  return t instanceof Element && Boolean(t.closest('a, button, input, textarea, select, label, img, video, [role="button"], [contenteditable="true"], [data-no-reply]'));
}

/** Arrow-key navigation between message rows in the same list. */
export function focusSibling(from: HTMLElement, dir: 1 | -1 | 'first' | 'last') {
  const list = Array.from((from.closest('[data-message-list]') ?? document).querySelectorAll<HTMLElement>('[data-message-row]'));
  const i = list.indexOf(from);
  const next = dir === 'first' ? list[0] : dir === 'last' ? list[list.length - 1] : list[i + dir];
  if (next) { next.focus(); next.scrollIntoView({ block: 'nearest' }); }
  return next ?? null;
}

/** Keyboard entry into a message list: Up / End go to the newest message, Down / Home to the oldest. */
export function onListKeyDown(e: React.KeyboardEvent<HTMLElement>) {
  if (e.target !== e.currentTarget) return;
  const rows = e.currentTarget.querySelectorAll<HTMLElement>('[data-message-row]');
  if (!rows.length) return;
  const last = e.key === 'ArrowUp' || e.key === 'End', first = e.key === 'ArrowDown' || e.key === 'Home';
  if (!last && !first) return;
  e.preventDefault();
  const el = last ? rows[rows.length - 1] : rows[0];
  el.focus(); el.scrollIntoView({ block: 'nearest' });
}

/** R while hovering a message (and not typing anywhere) replies to it. */
let hovered: (() => void) | null = null;
let installed = false;
export function setHoveredReply(fn: (() => void) | null) {
  hovered = fn;
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('keydown', e => {
    if ((e.key !== 'r' && e.key !== 'R') || e.ctrlKey || e.metaKey || e.altKey || !hovered) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.closest('input, textarea, select, [contenteditable="true"], [role="dialog"][aria-modal="true"]') || t.hasAttribute('data-message-row'))) return;
    e.preventDefault();
    hovered();
  });
}
